import { useState, useMemo, useRef, useEffect } from "react";
import { useSpriteImages } from "@/hooks/useSpriteImages";

interface SpriteFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  description?: string;
}

/**
 * Sprite image/pattern picker. Shows a searchable grid of available sprites
 * fetched from the style's sprite.json.
 */
export function SpriteField({ label, value, onChange, description }: SpriteFieldProps) {
  const { images, loading, spriteUrl } = useSpriteImages();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return images;
    const q = search.toLowerCase();
    return images.filter((img) => img.name.toLowerCase().includes(q));
  }, [images, search]);

  const pngUrl = spriteUrl ? `${spriteUrl}.png` : null;

  return (
    <div className="py-spacing-field">
      <div className="flex items-center gap-2">
        <label className="w-32 shrink-0 text-xs text-text-secondary" title={description}>
          {label}
        </label>
        <div className="flex flex-1 items-center gap-1.5">
          {/* Preview of selected sprite */}
          {value && pngUrl && (
            <SpritePreview name={value} images={images} pngUrl={pngUrl} />
          )}
          <input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Sprite image name"
            className="h-7 flex-1 rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
          />
          {value && (
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="h-7 shrink-0 rounded-radius-sm border border-border-subtle bg-surface-base px-1.5 text-[10px] text-text-muted hover:text-error hover:bg-error-muted"
              title="Clear"
            >
              ✕
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            disabled={images.length === 0}
            className="h-7 shrink-0 rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-[10px] text-text-muted hover:bg-surface-sunken disabled:opacity-30"
            title={loading ? "Loading sprites..." : `${images.length} sprites available`}
          >
            {loading ? "..." : "Pick"}
          </button>
        </div>
      </div>

      {/* Sprite picker dropdown */}
      {open && images.length > 0 && (
        <div ref={pickerRef} className="mt-1.5 ml-32 rounded-radius-md border border-border-subtle bg-surface-raised p-2 shadow-lg">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${images.length} sprites...`}
            autoFocus
            className="mb-2 h-6 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-[10px] text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
          />
          <div className="grid max-h-48 grid-cols-4 gap-1 overflow-y-auto">
            {filtered.slice(0, 200).map((img) => (
              <button
                key={img.name}
                type="button"
                onClick={() => {
                  onChange(img.name);
                  setOpen(false);
                  setSearch("");
                }}
                className={`flex flex-col items-center gap-0.5 rounded-radius-sm p-1 transition-colors ${
                  value === img.name
                    ? "bg-accent-muted ring-1 ring-accent"
                    : "hover:bg-surface-sunken"
                }`}
                title={`${img.name} (${img.width}×${img.height})`}
              >
                {pngUrl && (
                  <div
                    className="h-6 w-6 shrink-0 bg-no-repeat"
                    style={{
                      backgroundImage: `url(${pngUrl})`,
                      backgroundPosition: `-${img.x}px -${img.y}px`,
                      backgroundSize: "auto",
                      imageRendering: "pixelated",
                    }}
                  />
                )}
                <span className="w-full truncate text-center text-[8px] text-text-muted">
                  {img.name}
                </span>
              </button>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="py-2 text-center text-[10px] text-text-muted">No sprites match "{search}"</div>
          )}
          {filtered.length > 200 && (
            <div className="mt-1 text-center text-[9px] text-text-muted">
              Showing 200 of {filtered.length} — narrow your search
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SpritePreview({
  name,
  images,
  pngUrl,
}: {
  name: string;
  images: { name: string; x: number; y: number; width: number; height: number }[];
  pngUrl: string;
}) {
  const entry = images.find((img) => img.name === name);
  if (!entry) return null;

  return (
    <div
      className="h-5 w-5 shrink-0 rounded-radius-sm border border-border-subtle bg-surface-sunken bg-no-repeat"
      style={{
        backgroundImage: `url(${pngUrl})`,
        backgroundPosition: `-${entry.x}px -${entry.y}px`,
        backgroundSize: "auto",
        imageRendering: "pixelated",
      }}
      title={`${name} (${entry.width}×${entry.height})`}
    />
  );
}
